import type { HomepageContent } from "@whitehaven/contracts/homepage";

export type HomepagePlainTextPath = string;

export interface HomepagePlainTextFieldMetadata {
  readonly label: string;
  readonly minLength: number;
  readonly maxLength: number;
  readonly control: "input" | "textarea";
  readonly rows?: number;
}

export const homepagePlainTextPaths = [] as const;
export const homepagePlainTextFieldMetadata = Object.freeze({}) as Readonly<
  Record<string, HomepagePlainTextFieldMetadata>
>;

const unchanged = Symbol("homepage-plain-text-unchanged");

function updateAtPath(
  node: unknown,
  segments: readonly string[],
  value: string,
): unknown | typeof unchanged {
  if (segments.length === 0) {
    return typeof node === "string" ? value : unchanged;
  }
  const [segment, ...remaining] = segments;
  if (segment === undefined || node === null || typeof node !== "object") {
    return unchanged;
  }
  if (Array.isArray(node)) {
    if (!/^\d+$/u.test(segment)) return unchanged;
    const index = Number(segment);
    if (!Number.isSafeInteger(index) || index < 0 || index >= node.length) {
      return unchanged;
    }
    const child = updateAtPath(node[index], remaining, value);
    if (child === unchanged) return unchanged;
    const updated = [...node];
    updated[index] = child;
    return updated;
  }
  if (!Object.hasOwn(node, segment)) return unchanged;
  const record = node as Readonly<Record<string, unknown>>;
  const child = updateAtPath(record[segment], remaining, value);
  if (child === unchanged) return unchanged;
  return { ...record, [segment]: child };
}

export function updateHomepagePlainText(
  content: HomepageContent,
  path: HomepagePlainTextPath,
  value: string,
): HomepageContent {
  const updated = updateAtPath(content, path.split("."), value);
  return updated === unchanged ? content : (updated as HomepageContent);
}
